import Backbone from 'backbone';
import d3Lucid from 'common/charts/d3-lucid';
import d3Lucid_minimalTimeseriesChart from 'common/charts/d3-lucid-minimal-timeseries-chart';
import AnalyticAppDisplayRule from 'common/constants/analytic-app-display-rule';
import * as AnalyticAppColor from 'common/constants/analytic-app-color';
import {
  AnalyticAppErrorMessageEnum,
  AnalyticAppInteractionEnum,
  AnalyticAppRendererEnum,
  ReportingPeriodEnum,
} from 'common/proxies/enum-proxy';
import Interpolation from 'constants/interpolation';
import {appendPx, removePx} from 'common/utils/dom-util';
import {getEnumBySlug} from 'common/utils/enum-util';
import {t} from 'common/utils/translation-util';

const ChartView = Backbone.View.extend({
  // Properties

  chart: d3Lucid_minimalTimeseriesChart(),

  // Methods

  getComparisonRenderer(series) {
    if (series && series.hasOwnProperty('renderer')) {
      switch (series.renderer.slug) {
        case AnalyticAppRendererEnum.COLUMN.slug:
          return AnalyticAppRendererEnum.PLOT;
        default:
          return AnalyticAppRendererEnum.LINE;
      }
    }
  },

  getComparisonSeries(collection, unit, mainSeries) {
    const color = this.color;
    const systemName = this.systemName;
    if (collection.hasResults(systemName)) {
      const series = d3Lucid.series.getTimeseries();
      series.displayName = t('Previous period');
      series.end = collection.getEndTimestamp();
      series.fill = color;
      series.interaction = AnalyticAppInteractionEnum.WHOLE_CHART;
      series.interpolate = Interpolation.CARDINAL;
      series.renderer = this.getComparisonRenderer(mainSeries);
      series.start = collection.getStartTimestamp();
      series.stroke = color;
      series.strokeStyle = 'dashed';
      series.unit = unit;
      series.values = this.getValues(collection, systemName);
      return series;
    }
  },

  getData(collections, period, point) {
    const collection1 = collections[1];
    const collection2 = collections[3];
    const unit = this.getUnit(period, point);
    const displayName = point.displayName;
    const type = point.type;
    const series = this.getSeries(collection1, period, unit, displayName, type);
    const data = [series];
    if (collection2) {
      const comparison = this.getComparisonSeries(collection2, unit, series);
      data.push(comparison);
    }
    return data;
  },

  getDimensions() {
    const widthPx = this.$el.css('width');
    const width = removePx(widthPx);
    const height = Math.round(width * 0.45);
    const heightPx = appendPx(height);
    return {
      height,
      heightPx,
      width,
      widthPx,
    };
  },

  getRenderer(period, type) {
    const rules = getEnumBySlug(type.slug, AnalyticAppDisplayRule);
    const rulesForPeriod = rules.periods[period.slug];
    const series = rulesForPeriod.series.slug;
    return getEnumBySlug(series, AnalyticAppRendererEnum);
  },

  getSeries(collection, period, unit, displayName, type) {
    const color = this.color;
    const systemName = this.systemName;
    if (collection.hasResults(systemName)) {
      const series = d3Lucid.series.getTimeseries();
      series.displayName = displayName;
      series.end = collection.getEndTimestamp();
      series.fill = color;
      series.fillOpacity = 0.5;
      series.gridLineStroke = AnalyticAppColor.WHITE.primary;
      series.gridLineStrokeOpacity = 0.75;
      series.interaction = AnalyticAppInteractionEnum.WHOLE_CHART;
      series.interpolate = Interpolation.CARDINAL;
      series.start = collection.getStartTimestamp();
      series.stroke = color;
      series.renderer = this.getRenderer(period, type);
      series.unit = unit;
      series.values = this.getValues(collection, systemName);
      return series;
    }
  },

  getUnit(period, point) {
    const {integrated, rate} = point.units;
    switch (period.slug) {
      case ReportingPeriodEnum.MONTH.slug:
      case ReportingPeriodEnum.YEAR.slug:
        return integrated;
      default:
        return rate;
    }
  },

  getValues(collection, systemName) {
    const values = [];
    const results = collection.getResults();
    for (let ii = 0, nn = results.length; ii < nn; ii++) {
      const result = results[ii];
      if (result[systemName]) {
        const reading = {
          timestamp: result.startTimestamp,
          value: result[systemName].value,
        };
        values.push(reading);
      }
    }
    return values;
  },

  initialize(options) {
    this.color = AnalyticAppColor.WHITE.primary;
    const configuration = options.settings.configuration;
    if (configuration && configuration.graph_hex) {
      this.color = `#${configuration.graph_hex}`;
    }
    this.systemName = options.systemName;
    const dimensions = this.getDimensions();
    this.$el.css('height', dimensions.heightPx);
    this.chart
      .selection(options.el.selector)
      .period(ReportingPeriodEnum.TODAY)
      .height(dimensions.height)
      .width(dimensions.width)
      .state(d3Lucid.states.INIT)();
  },

  render(collections, period, point) {
    const data = this.getData(collections, period, point);
    let values = [];
    if (data[0] && data[0].hasOwnProperty('values')) {
      values = data[0].values;
    }
    if (values.length) {
      this.chart.data(data).period(period).state(d3Lucid.states.NORMAL)();
    } else {
      this.showMessageState(AnalyticAppErrorMessageEnum.NO_DATA.message);
    }
  },

  resize() {
    const dimensions = this.getDimensions();
    this.chart.height(dimensions.height).width(dimensions.width)();
    this.$el.css('height', dimensions.heightPx);
  },

  showLoadingState() {
    this.chart.state(d3Lucid.states.LOADING)();
  },

  showMessageState(message) {
    this.chart.message(message).state(d3Lucid.states.MESSAGE)();
  },
});

export default ChartView;
